/**
 * The main js file for the theme
 *
 * This is the entry point where all modules are imported and compiled.
 *
 * @package Embark
 * @since   1.0.0
 */

/**
 * Critical early stuff.
 */
import { Breakpoints } from './theme/breakpoints';
import {
	imageLinkQuerySelector, videoLinkQuerySelector, youtubeRegex, youtubePageRegex,
} from './theme/lightboxes/helpers';

/**
 * First things first: Trigger an event indicating that jQuery is loaded. If it's loaded.
 */
if (typeof jQuery === 'function') {
	window.dispatchEvent(new CustomEvent('jqueryloaded'));
}

class Embark {
	// Object to store components in order to expose them to other components.
	components = {};

	/**
	 * Set variables for common elements, fonts, breakpoints and dynamically
	 * import all modules for the project.
	 */
	constructor() {
		/**
		 * Common elements used throughout the project.
		 */
		this.els = {
			html: document.documentElement,
			body: document.body,
			header: document.getElementById('js-site-header'),
			headerSearch: document.querySelector('.site-header .search-form'),
			navContainer: document.querySelector('.site-header__navigation'),
			mainMenu: document.querySelector('.main-menu'),
			navicon: document.getElementById('js-navicon'),
			naviconTrigger: document.getElementById('js-navicon-trigger'),
		};

		/**
		 * Each object in this array has the following parameters:
		 * path: The filepath, without file extension, to import relative to "./theme/".
		 * test: A test to check if the module should be imported or not. Typically,
		 * this is simply checking to see if an element exists in the DOM.
		 */
		this.imports = [
			{
				path: 'sticky-header',
				test: this.els.header,
			},
			{
				path: 'site-banner',
				test: document.querySelector('.site-alert-banner'),
			},
			{
				path: 'main-menu',
				test: this.els.mainMenu,
			},
			{
				path: 'scrollfade',
				test: document.querySelector('.scrollfade'),
			},
			{
				path: 'filterable-events',
				test: document.querySelector('.nstg-block-filterable-events'),
			},
			{
				path: 'filterable-content',
				test: document.querySelector('.em-filterable-content'),
			},
			{
				path: 'forms',
				test: document.querySelector('input, select'),
			},
			{
				path: 'block-sliders',
				test: document.querySelector('.em-featured-solutions__content-container, .em-related-products__content, .em-resource-posts-slider__content, .slick'),
			},
			{
				path: 'search-form',
				test: document.getElementsByTagName('header')[0].querySelector('.search-form'),
			},
			{
				path: 'scroll-to',
				test: document.querySelector('a[href*="#"]') || window.location.hash,
			},
			{
				path: 'tables',
				test: document.querySelector('table') || document.querySelector('.em-tables__table'),
			},
			{
				path: 'block-accordions',
				test: document.querySelector('.em-accordion-publications__accordion-container, .board-people__accordion'),
			},
			{
				path: 'careers-slider',
				test: document.querySelector('.em-careers'),
			},
			{
				path: 'tabbed-content-block',
				test: document.querySelector('.em-tabbed-content__tabs-container'),
			},
			{
				path: 'video-slider-block',
				test: document.querySelector('.em-video-slider__slider'),
			},
			{
				path: 'favorite-content',
				test: document.querySelector('.em-favorite-content-listings, .favorite-post-toggle'),
			},
			{
				path: 'yoast-faq',
				test: document.querySelector('.wp-block-yoast-faq-block'),
			},
			{
				path: 'block-flipper',
				test: document.querySelector('.wp-block-nstg-flipper'),
			},
			{
				path: 'lightboxes',
				test: (() => {
					const els = [...document.querySelectorAll(`${imageLinkQuerySelector}, ${videoLinkQuerySelector}, .em-filterable-content`)];
					if (els.length) {
						const filtered = els.filter((el) => {
							// Don't initialize lightboxes for YouTube profile or channel links.
							if (el.nodeName === 'A' && el.href.match(youtubeRegex) && el.href.match(youtubePageRegex)) {
								return false;
							}

							return true;
						});

						return filtered.length > 0;
					}

					return false;
				})(),
			},
		];

		/**
		 * Array of font-family names to observe with FontFaceObserver to ensure they load.
		 */
		this.fonts = ['proxima-nova'];

		/**
		 * Breakpoints used throughout the project. These should match up with
		 * the breakpoints defined in src/scss/config/mixins/_media-queries.scss.
		 */
		this.breakpoints = new Breakpoints([
			{
				key: 'xs',
				value: 480,
			},
			{
				key: 'sm',
				value: 640,
			},
			{
				key: 'md',
				value: 850,
			},
			{
				key: 'lg',
				value: 1120,
			},
			{
				key: 'xl',
				value: 1380,
			},
			{
				key: 'menu',
				value: 1000,
			},
		]);

		this.init();
	}

	init = () => {
		this.importModules();
	}

	/**
	 * Load these modules only when they are needed.
	 *
	 * @since 2.1.1
	 */
	importModules = () => {
		this.imports.forEach(({ path, test }) => {
			if (test) {
				import(
					/* webpackChunkName: "[request]" */
					`./theme/${path}`
				);
			}
		});
	}
}

document.addEventListener('DOMContentLoaded', () => {
	window.embark = new Embark();
});
